import React, { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import Card from "../../../components/UI/card/Card";
import { Formik, Field, Form, ErrorMessage, FormikHelpers } from "formik";
import * as Yup from "yup";
import { ROUTES } from "../../../constants/routes";
import { getApi } from "../../../services/http.service";
import { IEmployee } from "../../../interfaces/employee.interface";
import { IDistributor } from "../../../interfaces/distributor.interface";
import { IBeat } from "../../../interfaces/beat.interface";
import { createNewBeat, updateBeatById } from "../../../services/beat.service";
import { STATES_OPTIONS } from "../../../constants/states";
import { toast } from "react-toastify";

const validationSchema = Yup.object({
  beatName: Yup.string().required("Beat name is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  employee: Yup.string().required("Employee is required"),
  distributor: Yup.string().required("Distributor is required"),
});

const BeatForm: React.FC<any> = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [distributors, setDistributors] = useState<IDistributor[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [beat, setBeat] = useState<IBeat>();

  const navigate = useNavigate();

  useEffect(() => {
    if (params.get("beatId")) fetchBeat();
  }, [params.get("beatId")]);

  const fetchBeat = async () => {
    try {
      const response = await getApi(ROUTES.BEAT_BY_ID + params.get("beatId"));
      if (response.code === 200) {
        setBeat(response?.data);
        await fetchDistributor(response?.data?.state);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const fetchDistributor = async (state: string) => {
    try {
      const [employeesData, distributorsData] = await Promise.all([
        getApi(ROUTES.EMPLOYEE_LIST_BY_FILTER, { state }),
        getApi(ROUTES.DISTRIBUTOR_LIST_BY_FILTER, { state }),
      ]);
      setEmployees(employeesData.data || []);
      setDistributors(distributorsData.data || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <div className="w-100">
      <div className="row justify-content-center">
        <div className="col-lg-12 col-md-12 col-sm-12 w-100">
          <Card>
            <div className="product__edit p-4 w-100">
              <div className="d-flex">
                <Link className="nav-link" to={"/beats"}>
                  <Icon
                    icon="material-symbols:arrow-back-rounded"
                    fontSize={25}
                  />
                </Link>
                <h4 className="ms-2">Beats</h4>
              </div>

              {/* Formik Form */}
              <Formik
                initialValues={{
                  beatName: beat?.beatName || "",
                  state: beat?.state || "",
                  city: beat?.city || "",
                  employee:
                    typeof beat?.employee == "object"
                      ? beat?.employee?._id
                      : "",
                  distributor: typeof beat?.distributor == "object"
                      ? beat?.distributor?._id
                      : "",
                }}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={async (
                  values,
                  { setFieldValue }: FormikHelpers<IBeat>
                ) => {
                  if (params?.get("beatId")) {
                    const payload = {
                      ...values,
                    };
                    const result = await updateBeatById(
                      params?.get("beatId"),
                      payload
                    );
                    if (result) {
                      toast.success("Beat updated successfully!");
                      navigate("/beats")
                    }
                  } else {
                    const result = await createNewBeat(values);
                    if (result) {
                      setFieldValue("beatName","");
                      setFieldValue("city","");
                      setFieldValue("employee","");
                      setFieldValue("distributor","");
                      toast.success("Beat added successfully!");
                    }
                  }
                }}
              >
                {({ errors, touched, values, setFieldValue }) => (
                  <Form className="row">
                    {/* Full Name */}
                    <div className="col-md-6 col-sm-12 mb-3">
                      <div className="form-group p-0">
                        <Field
                          id="beatName"
                          name="beatName"
                          type="text"
                          placeholder="Beat Name"
                          className={`form-control ${
                            errors.beatName && touched.beatName
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          name="beatName"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                    {/* Shop State */}
                    <div className="col-md-6 col-sm-12 mb-3">
                      <div className="form-group p-0">
                        <select
                          id="state"
                          name="state"
                          className={`form-control ${
                            errors.state && touched.state ? "is-invalid" : ""
                          }`}
                          value={values.state}
                          onChange={async (ev) =>{
                            setFieldValue("state", ev.target.value);
                            await fetchDistributor(
                              ev.target.value
                            );
                           }
                          }
                        >
                          <option value={""} disabled>
                            Select State
                          </option>
                          {STATES_OPTIONS.map((item) => (
                            <option key={item?.state}>{item.state}</option>
                          ))}
                        </select>
                        <ErrorMessage
                          name="state"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                    {/* Shop City */}
                    <div className="col-md-6 col-sm-12 mb-3">
                      <div className="form-group p-0">
                        <select
                          id="city"
                          name="city"
                          value={values.city}
                          className={`form-control ${
                            errors.city && touched.city ? "is-invalid" : ""
                          }`}
                          onChange={ (ev) => {
                            setFieldValue("city", ev.target.value);
                          }}
                        >
                          <option value={""} disabled>
                            Select City
                          </option>
                          {STATES_OPTIONS.find(
                            (_: any) => _.state === values.state
                          )?.districts.map((item) => (
                            <option key={item}>{item}</option>
                          ))} 
                        </select>
                        <ErrorMessage
                          name="city"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                    {/* employee */}
                    <div className="col-md-6 col-sm-12 mb-3">
                      <div className="form-group p-0">
                        <select
                          id="employee"
                          name="employee"
                          className={`form-control ${
                            errors.employee && touched.employee
                              ? "is-invalid"
                              : ""
                          }`}
                          value={values?.employee?.toString()}
                          onChange={(ev) =>
                            setFieldValue("employee", ev.target.value)
                          }
                        >
                          <option value={""} disabled>
                            Select Employee
                          </option>
                          {employees.map((item) => (
                            <option key={item?._id} value={item?._id}>
                              {item.fullName}
                            </option>
                          ))}
                        </select>
                        <ErrorMessage
                          name="employee"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>

                    <div className="col-md-6 col-sm-12 mb-3">
                      <div className="form-group p-0">
                        <select
                          id="distributor"
                          name="distributor"
                          className={`form-control ${
                            errors.distributor && touched.distributor
                              ? "is-invalid"
                              : ""
                          }`}
                          value={values?.distributor?.toString()}
                          onChange={(ev) =>
                            setFieldValue("distributor", ev.target.value)
                          }
                        >
                          <option value={""} disabled>
                            Select Distributor
                          </option>
                          {distributors.map((item) => (
                            <option key={item?._id} value={item?._id}>
                              {item.firmName}
                            </option>
                          ))}
                        </select>
                        <ErrorMessage
                          name="distributor"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                    <div className="d-flex gap-3 mt-2 col-md-12">
                      <button type="submit" className="btn btn-primary px-4">
                        Submit
                      </button>
                      <Link to="/beats">
                        <button className="btn btn-secondary px-4">
                          {t("cancel")}
                        </button>
                      </Link>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BeatForm;
